import {
  ChakraBox,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  ModalV2,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import { PatientCalendarEvents } from '~/analytics/patientsCalendarEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

import {
  useRegisterOrUnregisterPatientOrSupportFromGroupSectionMutation,
  useUnregisterPatientOrSupportMutation,
} from '~/store/services/patients';

interface UnregisterModalProps {
  groupClassExternalId: string;
  groupSectionId?: string;
  isOpen: boolean;
  onClose: () => void;
  patientId?: string;
  userId: string;
}

const UnregisterPatientModal = ({
  groupClassExternalId,
  groupSectionId,
  isOpen,
  onClose,
  patientId,
  userId,
}: UnregisterModalProps) => {
  const TOP_LEVEL_ID = 'group-class-unregister-modal';
  const { trackEvent } = useAnalytics();
  const { createToast } = useToastMessage();

  const [unregisterGroupClass, { isLoading }] =
    useUnregisterPatientOrSupportMutation();
  const [unregisterFromGroupSection, { isLoading: isV2Loading }] =
    useRegisterOrUnregisterPatientOrSupportFromGroupSectionMutation();

  const isLoadingState = isLoading || isV2Loading;

  const handleUnregister = () => {
    trackEvent(
      PatientCalendarEvents.unRegisterConformationModalUnRegisterUserButtonClicked,
      {
        Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
      },
    );
    const unregisterAction =
      patientId && groupSectionId
        ? unregisterFromGroupSection({
            groupSectionId,
            patientId,
            userExternalId: userId,
            isRegistered: false,
          })
        : unregisterGroupClass({ groupClassExternalId, userId });

    unregisterAction
      .unwrap()
      .then(() => {
        createToast({
          icon: FAIconName.checkRegular,
          id: 'unregister-patient-or-support-success-toast',
          text: 'User unregistered successfully',
          variant: 'success',
        });
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'unregister-patient-or-support-error-toast',
          text: 'Failed to unregister user',
          variant: 'critical',
        });
      })
      .finally(() => {
        onClose();
      });
  };

  return (
    <ModalV2
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel-button`}
            leftIcon={FAIconName.timesRegular}
            onClick={onClose}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__unregister-button`}
            isDisabled={isLoadingState}
            isLoading={isLoadingState}
            leftIcon={FAIconName.checkRegular}
            onClick={handleUnregister}
          >
            Unregister user
          </FireflyButton>
        </ChakraHStack>
      }
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Are you sure?"
    >
      <ChakraBox>
        <Text id={`${TOP_LEVEL_ID}__warning-text`} variant="body">
          Once you unregister this user, all future events in this series will
          be removed from their calendar.
        </Text>
      </ChakraBox>
    </ModalV2>
  );
};

export default UnregisterPatientModal;
